import { motion } from 'framer-motion'
import { Sparkles, ArrowRight, Zap } from 'lucide-react'
import { ReactNode } from 'react'

interface PromoCardProps {
  title: string
  subtitle: string
  description: string
  buttonText: string
  buttonLink?: string
  icon?: ReactNode
  gradient?: string
}

export default function PromoCard({
  title,
  subtitle,
  description,
  buttonText,
  buttonLink = '#',
  icon = <Sparkles className="w-6 h-6" />,
  gradient = 'from-purple-600 via-pink-600 to-orange-500',
}: PromoCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative"
    >
      <div className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 dark:from-slate-950 dark:via-purple-950 dark:to-slate-950 rounded-2xl overflow-hidden border border-purple-500/20 shadow-xl">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-orange-600/10 opacity-50" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)`,
              backgroundSize: '2rem 2rem',
            }}
          />
        </div>

        {/* Glowing orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl" />
        
        <div className="relative z-10 p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Left side - Content */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white">
                  {icon}
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-full">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-semibold text-purple-300">{subtitle}</span>
                </div>
              </div>
              
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">
                {title}
              </h2>
              
              <p className="text-sm md:text-base text-gray-300 max-w-2xl mb-0">
                {description}
              </p>
            </div>

            {/* Right side - CTA */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0"
            >
              <a
                href={buttonLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold text-base shadow-lg shadow-purple-500/25 transition-all duration-300 whitespace-nowrap"
              >
                {buttonText}
                <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
